import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AppService } from '../app.service';
import { fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class KeepAliveService implements OnDestroy {
  private readonly PING_INTERVAL_TIME = (window as any).KEEP_ALIVE_PING || environment.keepAlive?.pingInterval || (20 * 60 * 1000);
  private readonly IDLE_TIMEOUT_TIME = (window as any).KEEP_ALIVE_IDLE || environment.keepAlive?.idleTimeout || (60 * 60 * 1000);
  
  private pingTimer: any;
  private idleTimer: any;
  private isIdle = false;
  private eventSubscription: Subscription;
  private running = false;

  constructor(
    private appService: AppService, 
    private ngZone: NgZone
  ) {}

  start() {
    console.log('KeepAliveService: start() called. running:', this.running);
    if (this.running) {
      console.log('KeepAliveService: Already running.');
      return;
    }
    
    console.log('KeepAliveService: Starting... interval:', this.PING_INTERVAL_TIME);
    this.running = true;

    // Use NgZone.runOutsideAngular to prevent triggering change detection on every user event
    this.ngZone.runOutsideAngular(() => {
      const events$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'touchstart')
      ).pipe(
        throttleTime(1000) // Process at most once per second
      );

      this.eventSubscription = events$.subscribe(() => {
        this.resetIdleTimer();
      });

      // Initialize timers
      console.log('KeepAliveService: Initializing timers. Ping interval:', this.PING_INTERVAL_TIME, 'Idle timeout:', this.IDLE_TIMEOUT_TIME);
      this.resetIdleTimer();
      this.startPingTimer();

      // Trigger an immediate ping if already logged in (e.g. after refresh)
      this.ping();
    });
  }

  stop() {
    console.log('KeepAliveService: Stopping...');
    this.running = false;
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
      this.eventSubscription = null;
    }
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.stopPingTimer();
  }

  private resetIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
    
    if (this.isIdle) {
      // Scenario C: User returns from idle
      this.isIdle = false;
      this.ping(); // Immediate wake-up ping
      this.startPingTimer(); // Resume recurring interval
    }

    // Scenario A: Set or reset the idle timeout
    this.idleTimer = setTimeout(() => {
      // Scenario B: User becomes idle
      this.isIdle = true;
      this.stopPingTimer();
    }, this.IDLE_TIMEOUT_TIME);
  }

  private startPingTimer() {
    this.stopPingTimer();
    this.pingTimer = setInterval(() => {
      this.ping();
    }, this.PING_INTERVAL_TIME);
  }

  private stopPingTimer() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private ping() {
    console.log('KeepAliveService: ping() called. loginInfo exists:', !!this.appService.loginInfo);
    // Run inside Angular zone to ensure standard service behavior and observability
    this.ngZone.run(() => {
      // Only ping if user is logged in
      if (this.appService.loginInfo) {
        const url = this.appService.generateGetUrl('shareapi', 'pingTongueAI');
        console.log('KeepAliveService: Sending background ping to:', url);
        this.appService.get('shareapi', 'pingTongueAI').subscribe({
          next: () => {
            console.log('KeepAliveService: Background ping successful.');
          },
          error: (err) => {
            console.error('KeepAliveService: Background AI ping failed', err);
          }
        });
      } else {
        console.log('KeepAliveService: Ping skipped (user not logged in).');
      }
    });
  }

  ngOnDestroy() {
    this.stop();
  }
}
